// src/contexts/RestaurantContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import PropTypes from "prop-types";

const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error("useRestaurant must be used within a RestaurantProvider");
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const [currentRestaurant, setCurrentRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useParams();
  const location = useLocation();

  useEffect(() => {
    const loadRestaurant = async () => {
      setLoading(true);

      try {
        const hostname = window.location.hostname;
        const isLocalhost =
          hostname === "localhost" || hostname === "127.0.0.1";

        // Extract restaurant slug from URL
        const restaurantSlug = params.restaurantSlug;

        if (restaurantSlug) {
          // Multi-tenant route: /restaurant/:restaurantSlug/*
          const restaurant = await fetchRestaurantBySlug(restaurantSlug);
          setCurrentRestaurant(restaurant);
        } else if (isLocalhost) {
          // On localhost, default to Lucky Dog Tavern for owner testing
          const luckyDogRestaurant = await fetchRestaurantBySlug(
            "lucky-dog-tavern"
          );
          setCurrentRestaurant(luckyDogRestaurant);
        } else {
          // On production domain, no default restaurant (show landing page)
          setCurrentRestaurant(null);
        }
      } catch (error) {
        console.error("Error loading restaurant:", error);
        setCurrentRestaurant(null);
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [params.restaurantSlug, location.pathname]);

  const fetchRestaurantBySlug = async (slug) => {
    try {
      // First try to get restaurant by slug from the restaurants collection
      const restaurantRef = doc(db, "restaurants", slug);
      const restaurantSnap = await getDoc(restaurantRef);

      if (restaurantSnap.exists()) {
        return {
          id: restaurantSnap.id,
          slug: restaurantSnap.id,
          ...restaurantSnap.data(),
        };
      }

      // If not found and it's Lucky Dog Tavern, return a default config
      if (slug === "lucky-dog-tavern") {
        return {
          id: "lucky-dog-tavern",
          slug: "lucky-dog-tavern",
          name: "Lucky Dog Tavern",
          description: "A Local Favorite Since 2019",
          enterpriseAccess: true,
          menuTypes: ["wine", "beer", "cocktails", "spirits"],
          status: "active",
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      return null;
    }
  };

  const value = useMemo(
    () => ({
      currentRestaurant,
      loading,
      setCurrentRestaurant,
      fetchRestaurantBySlug,
    }),
    [currentRestaurant, loading]
  );

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};

RestaurantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Export the context for direct use if needed
export { RestaurantContext };
